import { useCallback, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import { ErrorLike } from '@apollo/client'
import { isErrorLike } from '@apollo/client/errors'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import { TagSelect } from '@/components/ui/TagSelect'
import Dropdown from '@/components/ui/Dropdown'
import { ApolloErrorMessage } from '@/components/ui/ApolloErrorMessage'

import {
  DropdownField,
  SelectField,
  StepComponentProps,
  TextField
} from '../types'

function StandardInput({ field }: { field: TextField }) {
  const { t } = useLingui()

  return (
    <Form.Field
      name={field.name}
      label={field.label}
      description={field.description}
      rules={{
        validate: field.required
          ? {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          : {}
      }}
    >
      {({ renderProps: { onChange, ...props } }) => (
        <Input
          type={field.inputType ?? 'text'}
          onChange={event => onChange(event.target.value)}
          placeholder={field.placeholder}
          {...props}
        />
      )}
    </Form.Field>
  )
}

function SelectInput({ field }: { field: SelectField }) {
  const { t } = useLingui()

  return (
    <Form.Field
      name={field.name}
      label={field.label}
      description={field.description}
      rules={{
        required: field.required ? t`This field is required` : undefined
      }}
    >
      {({ renderProps: props }) => (
        <TagSelect
          multiple={field.multi}
          options={field.options ?? []}
          {...props}
        />
      )}
    </Form.Field>
  )
}

function DropdownInput({ field }: { field: DropdownField }) {
  const { t } = useLingui()

  return (
    <Form.Field
      name={field.name}
      label={field.label}
      description={field.description}
      rules={{
        required: field.required ? t`This field is required` : undefined
      }}
    >
      {({ renderProps: props }) => (
        <Dropdown
          options={field.options ?? []}
          placeholder={field.placeholder}
          {...props}
        />
      )}
    </Form.Field>
  )
}

export function FormComponent({ step, onSubmit }: StepComponentProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorLike | null>(null)

  const onSubmitProxy = useCallback(
    (data: GraphQL.Scalars['Json']['input']) => {
      setLoading(true)

      onSubmit(data)
        .catch(error => {
          if (isErrorLike(error)) setError(error)
        })
        .finally(() => setLoading(false))
    },
    [setLoading, onSubmit]
  )

  return (
    <div className="flex flex-col gap-2 p-4">
      <h1 className="text-heading1 text-text">{step.title}</h1>
      {step.description && (
        <p className="text-regular text-text">{step.description}</p>
      )}
      <Form.Root onSubmit={onSubmitProxy}>
        <div className="flex flex-col gap-2.5 p-4">
          {step.fields.map(function (field, idx) {
            switch (field.type) {
              case 'text':
                return <StandardInput key={idx} field={field} />
              case 'select':
                return <SelectInput key={idx} field={field} />
              case 'dropdown':
                return <DropdownInput key={idx} field={field} />
            }
          })}
          <ApolloErrorMessage error={error} />
          <div className="flex flex-row items-center justify-end">
            <Form.Submit loading={loading}>
              <Trans>Submit</Trans>
            </Form.Submit>
          </div>
        </div>
      </Form.Root>
    </div>
  )
}
